import { type FC, useRef, useEffect } from 'react';
import { useAudioRecorderContext } from '@/app/lib/contexts';
import { realtimeVoiceLevels } from '@/app/lib/services/voice-realtime.service';
import type { RgbColor } from '../../utils/theme-color.ts';
import { readWaveformTheme } from './waveform-theme.ts';

/** Level conditioning (same idea as the builder's VoiceArcVisualizer):
 *  normalize against a rolling peak so quiet voices and hot mics both use the
 *  full range, then smooth asymmetrically — fast attack (jumps with speech),
 *  slow release (settles, not flickers). */
const PEAK_DECAY = 0.997;
const PEAK_FLOOR = 0.04;
const ATTACK = 0.25;
const RELEASE = 0.06;
const SPEECH_GATE = 0.06;

// Amplitude Configuration
// Controls the height/intensity of the waveform
const AMPLITUDE: {
  IDLE: number;
  READY: number;
  ACTIVE_MIN: number;
  ACTIVE_MAX: number;
  HEIGHT_MULTIPLIER: number;
  SMOOTHING_FACTOR: number;
} = {
  // Target amplitudes for different states [Range: 0-1, normalized]
  IDLE: 0.03, // Nearly flat when completely idle [Recommended: 0.01-0.1]
  READY: 0.07, // Small waves when ready but not active [Recommended: 0.05-0.3]
  ACTIVE_MIN: 0.2, // Minimum amplitude when speaking/recording [Recommended: 0.1-0.3]
  ACTIVE_MAX: 0.95, // Maximum amplitude when speaking/recording [Range: 0-1, Recommended: 0.7-1.0]

  // Visual scaling [Range: 0-1, normalized to canvas height]
  HEIGHT_MULTIPLIER: 0.2, // Wave height as fraction of canvas [Recommended: 0.15-0.3]

  // Controls how fast amplitude responds to volume changes
  // Too low (0.05): laggy, disconnected feeling
  // Too high (0.8): twitchy, jittery
  SMOOTHING_FACTOR: 0.4, // [Recommended: 0.15-0.5]
};

// Animation Speed Configuration
// Controls how fast the wave moves horizontally [Range: 0-1+, arbitrary units]
// Higher = faster scrolling motion
const ANIMATION_SPEED = {
  IDLE: 0.02, // Slow drift when idle [Recommended: 0.01-0.05]
  ACTIVE_RECORDING: 0.12, // Speed when user is recording [Recommended: 0.05-0.15]
  AI_SPEAKING: 0.12, // Faster movement when AI is speaking [Recommended: 0.1-0.3]
};

// Wave Shape Configuration
// Multiple sine waves are layered together to create organic, natural-looking movement
const WAVE_SHAPE = {
  // Amplitude contribution from each wave layer [Range: 0-1, should sum close to 1.0]
  // These control how much each wave type affects the final shape
  PRIMARY_WAVE: 0.6, // Main wave - base movement [Recommended: 0.5-0.7]
  HARMONIC_WAVE: 0.22, // Fast detail layer - adds texture [Recommended: 0.15-0.3]
  SUB_WAVE: 0.18, // Slow depth layer - adds bass feel [Recommended: 0.1-0.25]

  // Frequency multipliers [Range: 0.1-10, relative to base frequency]
  // Control how fast/slow secondary waves oscillate compared to primary
  HARMONIC_FREQ_MULT: 2.3, // Harmonic frequency [Recommended: 1.5-3.0] (higher = tighter ripples)
  SUB_FREQ_MULT: 0.7, // Sub-wave frequency [Recommended: 0.3-0.9] (lower = broader undulation)

  // Speed multipliers [Range: 0.1-3.0, relative to animation speed]
  // Control how fast secondary waves travel compared to primary
  HARMONIC_SPEED_MULT: 1.2, // Harmonic travel speed [Recommended: 1.0-2.0]
  SUB_SPEED_MULT: 0.8, // Sub-wave travel speed [Recommended: 0.5-1.0]
};

// Visual Styling Configuration
const VISUAL = {
  // Wave positioning [Range: 0-1, normalized to canvas height]
  VERTICAL_POSITION: 0.25, // Vertical position on canvas [Recommended: 0.2-0.4]
  // 0 = top, 0.5 = middle, 1 = bottom

  // Edge tapering [Range: 0-0.5, normalized to canvas width]
  TAPER_WIDTH_PERCENT: 0.15, // Width of fade zone on each edge [Recommended: 0.1-0.25]
  // 0.15 = 15% fade on left and right sides

  // Rendering quality [Integer values]
  CURVE_STEPS_DIVISOR: 2, // Curve point density [Recommended: 2-4]
  // Lower = more points = smoother but slower
  CURVE_STEPS_MAX: 300, // Max calculation points [Recommended: 150-400]
  // Caps performance cost on wide screens

  // Glow effect [Pixel values for blur, normalized for radius]
  GLOW_BLUR_BASE: 20, // Base blur in pixels [Recommended: 15-30]
  GLOW_BLUR_DYNAMIC: 25, // Extra blur when active [Recommended: 15-40]
  GLOW_RADIUS_PERCENT: 0.6, // Glow spread [Range: 0-1, Recommended: 0.4-0.8]

  // Edge fade zones [Range: 0-0.5, normalized to canvas dimensions]
  EDGE_FADE_PERCENT: 0.2, // Horizontal edge fade [Recommended: 0.15-0.3]
  BOTTOM_FADE_START: 0.5, // Where bottom fade begins [Recommended: 0.3-0.7]
  // 0.5 = starts at half amplitude below centerline
};

// Audio Input Configuration
const AUDIO_INPUT = {
  // Microphone sensitivity [Range: 100-1000, multiplier]
  // Raw microphone levels are typically very small (0.001-0.01)
  // This multiplier scales them to usable amplitude range (0-1)
  MIC_SENSITIVITY: 400, // [Recommended: 300-600]
  // Lower = less sensitive, Higher = more reactive

  // AI speaking simulation (when no real audio) [Range: 0-1, normalized]
  AI_SIM_MIN: 0.2, // Min simulated volume [Recommended: 0.1-0.3]
  AI_SIM_MAX: 0.7, // Max simulated volume [Recommended: 0.5-0.9]

  // Simulation timing [Range: 50-500 milliseconds]
  AI_SIM_INTERVAL_MS: 100, // Volume update frequency [Recommended: 80-200]
};

const SPEAKING_COLOR_MIX = 1;
const COLOR_TRANSITION_MS = 320;
const SPEAKING_COLOR_HOLD_MS = 280;
const WORKING_TRANSITION_MS = 240;
const FRAME_MS = 1000 / 60;

function blendColor(from: RgbColor, to: RgbColor, mix: number): string {
  const r = Math.round(lerp(from.r, to.r, mix));
  const g = Math.round(lerp(from.g, to.g, mix));
  const b = Math.round(lerp(from.b, to.b, mix));
  return `rgba(${r}, ${g}, ${b}, `;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Smooth interpolation helper
const lerp = (start: number, end: number, factor: number) => {
  return start + (end - start) * factor;
};

// Smoothstep interpolation for smooth edge tapering
const smoothstep = (t: number) => {
  return t * t * (3 - 2 * t);
};

// ============================================================================
// COMPONENT
// ============================================================================

type Props = {
  isActive: boolean;
  isAISpeaking: boolean;
  /** Pending work keeps a steady ripple; live audio still drives louder peaks and speech color. */
  isWorking?: boolean;
};

/**
 * AdaptiveWaveform - Visualizes audio with smooth amplitude
 * Uses the agent's voice theme for listening and audible assistant speech.
 */
export const AdaptiveWaveform: FC<Props> = ({ isActive, isAISpeaking, isWorking = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const volumeRef = useRef<number>(0);
  const smoothedVolumeRef = useRef<number>(AMPLITUDE.READY);
  const peakRef = useRef<number>(PEAK_FLOOR);
  const timeRef = useRef<number>(0);

  const { subscribeToProgress, isRecording } = useAudioRecorderContext();
  const stateRef = useRef({ isActive, isAISpeaking, isRecording, isWorking });
  stateRef.current = { isActive, isAISpeaking, isRecording, isWorking };

  // Subscribe to real microphone volume when recording
  useEffect(() => {
    if (!isRecording) {
      return;
    }

    const handleProgress = (_audioBuffer: AudioBuffer, level: number) => {
      // Scale microphone input based on sensitivity setting
      const scaledLevel = Math.min(level * AUDIO_INPUT.MIC_SENSITIVITY, 1.0);
      volumeRef.current = scaledLevel;
    };

    const unsubscribeProgress = subscribeToProgress(handleProgress);
    return () => {
      unsubscribeProgress();
    };
  }, [subscribeToProgress, isRecording]);

  // Generate simulated volume for AI speaking
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isAISpeaking && !isRecording) {
      // Only simulate when AI is speaking and user is NOT recording
      interval = setInterval(() => {
        const level =
          Math.random() * (AUDIO_INPUT.AI_SIM_MAX - AUDIO_INPUT.AI_SIM_MIN) +
          AUDIO_INPUT.AI_SIM_MIN;
        volumeRef.current = level;
      }, AUDIO_INPUT.AI_SIM_INTERVAL_MS);
    } else if (!isRecording) {
      volumeRef.current = 0;
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAISpeaking, isRecording]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d', {
      alpha: true,
    });
    if (!ctx) {
      return;
    }

    let width = 0;
    let height = 0;
    let dpr = 1;
    let speakingMix = 0;
    let workingMix = 0;
    let lastFrameAt = performance.now();
    let lastSpeechAt = -Infinity;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let theme = readWaveformTheme(getComputedStyle(document.documentElement));
    const themeObserver = new MutationObserver(() => {
      theme = readWaveformTheme(getComputedStyle(document.documentElement));
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      const pixelWidth = Math.round(width * dpr);
      const pixelHeight = Math.round(height * dpr);
      if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    };

    resizeCanvas();
    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(canvas);
    window.addEventListener('resize', resizeCanvas);

    const drawWave = (now: number) => {
      const { isActive, isAISpeaking, isRecording, isWorking } = stateRef.current;
      if (isAISpeaking || realtimeVoiceLevels.output > 0.005) {
        lastSpeechAt = now;
      }
      const targetMix = now - lastSpeechAt < SPEAKING_COLOR_HOLD_MS ? SPEAKING_COLOR_MIX : 0;
      // Cap resumed frames so returning from a background tab cannot jump the phase.
      const elapsed = Math.min(Math.max(now - lastFrameAt, 0), 50);
      const frameScale = elapsed / FRAME_MS;
      const colorStep = (elapsed / COLOR_TRANSITION_MS) * SPEAKING_COLOR_MIX;
      lastFrameAt = now;
      if (reducedMotion.matches) {
        speakingMix = targetMix;
      } else if (speakingMix < targetMix) {
        speakingMix = Math.min(targetMix, speakingMix + colorStep);
      } else {
        speakingMix = Math.max(targetMix, speakingMix - colorStep);
      }
      workingMix = reducedMotion.matches
        ? Number(isWorking)
        : lerp(workingMix, Number(isWorking), 1 - Math.exp(-elapsed / WORKING_TRANSITION_MS));
      const colors = {
        left: blendColor(theme.listening, theme.speaking, speakingMix),
        right: blendColor(theme.listening, theme.speaking, speakingMix),
        glow: blendColor(theme.listening, theme.speaking, speakingMix),
      };
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (width <= 0 || height <= 0) {
        animationRef.current = requestAnimationFrame(drawWave);
        return;
      }

      // Position waveform vertically on canvas
      const centerY = height * VISUAL.VERTICAL_POSITION;

      // Realtime sessions bypass the recorder pipeline, so their live levels
      // arrive through the level bus (mic + agent playback).
      const raw = Math.max(
        volumeRef.current,
        realtimeVoiceLevels.input,
        realtimeVoiceLevels.output,
      );
      peakRef.current = Math.max(peakRef.current * PEAK_DECAY ** frameScale, raw, PEAK_FLOOR);
      const activity = Math.min(1, raw / peakRef.current);
      const audioAmplitude =
        activity > SPEECH_GATE
          ? AMPLITUDE.ACTIVE_MIN + activity * (AMPLITUDE.ACTIVE_MAX - AMPLITUDE.ACTIVE_MIN)
          : AMPLITUDE.READY;
      // Work is independent of microphone noise; speech can rise above its gentle pulse.
      const workingAmplitude = reducedMotion.matches ? 0.3 : 0.3 + Math.sin(now / 450) * 0.06;
      const targetAmplitude = Math.max(audioAmplitude, workingAmplitude * workingMix);
      const smoothing = targetAmplitude > smoothedVolumeRef.current ? ATTACK : RELEASE;

      smoothedVolumeRef.current = lerp(
        smoothedVolumeRef.current,
        targetAmplitude,
        1 - (1 - smoothing) ** frameScale,
      );

      // Calculate actual pixel amplitude from normalized value
      const amplitude = height * AMPLITUDE.HEIGHT_MULTIPLIER * smoothedVolumeRef.current;

      // Wave properties
      const frequency = theme.frequency * lerp(1, 0.6, workingMix);
      let speed = ANIMATION_SPEED.IDLE;
      if (isRecording) {
        speed = ANIMATION_SPEED.ACTIVE_RECORDING;
      } else if (isAISpeaking) {
        speed = ANIMATION_SPEED.AI_SPEAKING;
      } else if (isActive) {
        speed = ANIMATION_SPEED.ACTIVE_RECORDING;
      }

      if (!reducedMotion.matches) {
        // One phase clock across work and speech, at the same speed on 60/120 Hz displays.
        timeRef.current += lerp(speed, FRAME_MS / 420, workingMix) * theme.speed * frameScale;
      }
      const time = timeRef.current;

      // Calculate wave taper for smooth edges
      const taperWidth = width * VISUAL.TAPER_WIDTH_PERCENT;
      const steps = Math.min(
        Math.floor(width / VISUAL.CURVE_STEPS_DIVISOR),
        VISUAL.CURVE_STEPS_MAX,
      );
      const stepSize = width / steps;
      const wavePoints: { x: number; y: number }[] = [];

      for (let i = 0; i <= steps; i++) {
        const x = i * stepSize;

        // Calculate taper factor (0 at edges, 1 in middle) with SMOOTHSTEP
        let edgeTaper = 1;
        if (x < taperWidth) {
          // Left edge: smoothstep interpolation for smooth curve
          const t = x / taperWidth;
          edgeTaper = smoothstep(t);
        } else if (x > width - taperWidth) {
          // Right edge: smoothstep interpolation for smooth curve
          const t = (width - x) / taperWidth;
          edgeTaper = smoothstep(t);
        }

        const taperedAmplitude = amplitude * edgeTaper;

        // Combine multiple sine waves for organic movement
        const y =
          centerY +
          Math.sin(x * frequency + time) * taperedAmplitude * WAVE_SHAPE.PRIMARY_WAVE +
          Math.sin(
            x * frequency * WAVE_SHAPE.HARMONIC_FREQ_MULT + time * WAVE_SHAPE.HARMONIC_SPEED_MULT,
          ) *
            taperedAmplitude *
            WAVE_SHAPE.HARMONIC_WAVE +
          Math.sin(x * frequency * WAVE_SHAPE.SUB_FREQ_MULT + time * WAVE_SHAPE.SUB_SPEED_MULT) *
            taperedAmplitude *
            WAVE_SHAPE.SUB_WAVE;

        wavePoints.push({ x, y });
      }

      // Helper function to draw smooth curve through points
      const drawSmoothCurve = () => {
        ctx.moveTo(wavePoints[0].x, wavePoints[0].y);

        // Draw curves through points using each point as control point
        for (let i = 0; i < wavePoints.length - 2; i++) {
          const xc = (wavePoints[i].x + wavePoints[i + 1].x) / 2;
          const yc = (wavePoints[i].y + wavePoints[i + 1].y) / 2;
          ctx.quadraticCurveTo(wavePoints[i].x, wavePoints[i].y, xc, yc);
        }

        // Draw final segment to last point
        const lastIdx = wavePoints.length - 1;
        ctx.quadraticCurveTo(
          wavePoints[lastIdx - 1].x,
          wavePoints[lastIdx - 1].y,
          wavePoints[lastIdx].x,
          wavePoints[lastIdx].y,
        );
      };

      // Draw glow layer - MAXIMUM OPACITY
      ctx.save();
      ctx.beginPath();
      drawSmoothCurve();

      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();

      const currentAmplitude = smoothedVolumeRef.current;
      const blurAmount = VISUAL.GLOW_BLUR_BASE + currentAmplitude * VISUAL.GLOW_BLUR_DYNAMIC;
      ctx.filter = `blur(${blurAmount}px)`;

      const glowGradient = ctx.createRadialGradient(
        width / 2,
        centerY,
        0,
        width / 2,
        centerY,
        width * VISUAL.GLOW_RADIUS_PERCENT,
      );

      const glowColor = colors.glow;
      // MAXIMUM opacity values - much more visible glow
      glowGradient.addColorStop(0, `${glowColor}${0.95 + currentAmplitude * 0.05})`);
      glowGradient.addColorStop(0.4, `${glowColor}${0.75 + currentAmplitude * 0.2})`);
      glowGradient.addColorStop(0.7, `${glowColor}0.45)`);
      glowGradient.addColorStop(1, `${glowColor}0.1)`);

      ctx.fillStyle = glowGradient;
      ctx.fill();

      ctx.restore();

      // Fade only the glow before drawing the line, so its blur has no hard
      // canvas edge and the wave stays crisp even at the highest speech peaks.
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      const topFade = ctx.createLinearGradient(0, 0, 0, centerY);
      topFade.addColorStop(0, 'rgba(0, 0, 0, 1)');
      topFade.addColorStop(0.25, 'rgba(0, 0, 0, 0.84)');
      topFade.addColorStop(0.5, 'rgba(0, 0, 0, 0.5)');
      topFade.addColorStop(0.75, 'rgba(0, 0, 0, 0.16)');
      topFade.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = topFade;
      ctx.fillRect(0, 0, width, centerY);
      ctx.restore();

      // Draw wave line with smooth curves - 100% OPACITY THROUGHOUT
      ctx.save();
      ctx.filter = 'none';
      ctx.beginPath();
      drawSmoothCurve();

      const lineGradient = ctx.createLinearGradient(0, 0, width, 0);

      lineGradient.addColorStop(0, `${colors.left}1)`);
      lineGradient.addColorStop(0.4, `${colors.left}1)`);
      lineGradient.addColorStop(0.5, `${colors.glow}1)`);
      lineGradient.addColorStop(0.6, `${colors.right}1)`);
      lineGradient.addColorStop(1, `${colors.right}1)`);

      if (workingMix > 0.001) {
        const sweep = reducedMotion.matches ? 0.5 : (now % 1800) / 1800;
        const workingGradient = ctx.createLinearGradient(0, 0, width, 0);
        const highlight = blendColor(
          theme.listening,
          theme.speaking,
          Math.max(speakingMix, workingMix),
        );
        workingGradient.addColorStop(0, `${colors.left}1)`);
        workingGradient.addColorStop(Math.max(0, sweep - 0.2), `${colors.left}1)`);
        workingGradient.addColorStop(sweep, `${highlight}1)`);
        workingGradient.addColorStop(Math.min(1, sweep + 0.2), `${colors.right}1)`);
        workingGradient.addColorStop(1, `${colors.right}1)`);
        ctx.strokeStyle = workingGradient;
      } else {
        ctx.strokeStyle = lineGradient;
      }

      ctx.lineWidth = theme.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();

      // Edge fades - using destination-out for proper transparency
      ctx.globalCompositeOperation = 'destination-out';

      // Left fade
      const leftFadeWidth = width * VISUAL.EDGE_FADE_PERCENT;
      const leftFade = ctx.createLinearGradient(0, 0, leftFadeWidth, 0);
      leftFade.addColorStop(0, 'rgba(0, 0, 0, 1)'); // Fully transparent at edge
      leftFade.addColorStop(0.3, 'rgba(0, 0, 0, 0.7)');
      leftFade.addColorStop(0.6, 'rgba(0, 0, 0, 0.4)');
      leftFade.addColorStop(1, 'rgba(0, 0, 0, 0)'); // Fully visible
      ctx.fillStyle = leftFade;
      ctx.fillRect(0, 0, leftFadeWidth, height);

      // Right fade
      const rightFadeStart = width * (1 - VISUAL.EDGE_FADE_PERCENT);
      const rightFade = ctx.createLinearGradient(rightFadeStart, 0, width, 0);
      rightFade.addColorStop(0, 'rgba(0, 0, 0, 0)'); // Fully visible
      rightFade.addColorStop(0.4, 'rgba(0, 0, 0, 0.4)');
      rightFade.addColorStop(0.7, 'rgba(0, 0, 0, 0.7)');
      rightFade.addColorStop(1, 'rgba(0, 0, 0, 1)'); // Fully transparent at edge
      ctx.fillStyle = rightFade;
      ctx.fillRect(rightFadeStart, 0, width * VISUAL.EDGE_FADE_PERCENT, height);

      // Bottom fade
      const bottomFadeStart = centerY + amplitude * VISUAL.BOTTOM_FADE_START;
      const bottomFade = ctx.createLinearGradient(0, bottomFadeStart, 0, height);
      bottomFade.addColorStop(0, 'rgba(0, 0, 0, 0)');
      bottomFade.addColorStop(0.3, 'rgba(0, 0, 0, 0.2)');
      bottomFade.addColorStop(0.6, 'rgba(0, 0, 0, 0.5)');
      bottomFade.addColorStop(0.85, 'rgba(0, 0, 0, 0.8)');
      bottomFade.addColorStop(1, 'rgba(0, 0, 0, 1)');
      ctx.fillStyle = bottomFade;
      ctx.fillRect(0, bottomFadeStart, width, height);

      ctx.restore();

      animationRef.current = requestAnimationFrame(drawWave);
    };

    animationRef.current = requestAnimationFrame(drawWave);

    return () => {
      resizeObserver.disconnect();
      themeObserver.disconnect();
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full pointer-events-none"
      style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  );
};
